import Swiper from 'swiper'
// https://github.com/import-js/eslint-plugin-import/issues/2266
// eslint-disable-next-line import/no-unresolved
import { Navigation, Pagination } from 'swiper/modules'

const FA_ICON_STYLE = 'fa-solid'
const FA_PREV_ICON = 'fa-angle-left'
const FA_NEXT_ICON = 'fa-angle-right'

const swiperInstances = new WeakMap()

const createSwiperInstance = (suiSwiper, swiperIndex) => {
  if (swiperInstances.has(suiSwiper)) return

  const prevButtonClass = `sui-swiper-prev-${swiperIndex}`
  const nextButtonClass = `sui-swiper-next-${swiperIndex}`
  const slides = Array.from(suiSwiper.querySelectorAll('.sui-swiper-slide'))
  const slideLabels = slides.map((slide) => slide.dataset.label)
  const hasPagination = suiSwiper.dataset.pagination === 'true'

  const getBreakpointsOptions = () => {
    const slidesPerView = Number(suiSwiper.dataset.slidesPerView) || 1

    return slidesPerView > 1
      ? {
          548: { slidesPerView: Math.max(slidesPerView - 2, 1) },
          768: { slidesPerView: Math.max(slidesPerView - 1, 1) },
          992: { slidesPerView },
        }
      : undefined
  }

  const getPaginationOptions = () =>
    hasPagination
      ? {
          el: `.swiper-pagination-${swiperIndex}`,
          renderBullet: (bulletIndex, bulletClassName) => {
            const bulletLabel = `Gehe zu Inhalt: ${slideLabels[bulletIndex] ?? bulletIndex + 1}`
            return `<button class="${bulletClassName}" aria-label="${bulletLabel}"></button>`
          },
          clickable: true,
        }
      : false

  const createBase = () => {
    const swiperWrapper = document.createElement('div')
    swiperWrapper.className = 'swiper-wrapper'
    while (suiSwiper.firstChild) {
      swiperWrapper.appendChild(suiSwiper.firstChild)
    }

    suiSwiper.appendChild(swiperWrapper)
    suiSwiper.classList.add(`swiper-container-${swiperIndex}`)
    suiSwiper.setAttribute('aria-roledescription', 'carousel')
    suiSwiper.setAttribute('aria-label', suiSwiper.dataset.label ?? 'Inhalte zum Durchblättern')
  }

  const addButtons = () => {
    ;[
      [[prevButtonClass, 'swiper-button-prev', 'swiper-button'], [FA_ICON_STYLE, FA_PREV_ICON], 'Vorheriger Inhalt'],
      [[nextButtonClass, 'swiper-button-next', 'swiper-button'], [FA_ICON_STYLE, FA_NEXT_ICON], 'Nächster Inhalt'],
    ].forEach(([buttonClasses, iconClasses, label]) => {
      const button = document.createElement('button')
      button.classList.add(...buttonClasses)
      button.setAttribute('aria-label', label)

      const icon = document.createElement('i')
      icon.classList.add(...iconClasses)
      icon.setAttribute('aria-hidden', 'true')

      // Adding an empty SVG to prevent Swiper from rendering its default icons
      const svg = document.createElement('svg')
      svg.style.display = 'none'

      icon.appendChild(svg)
      button.appendChild(icon)
      suiSwiper.appendChild(button)
    })
  }

  const addSlides = () => {
    slides.forEach((slide, slideIndex) => {
      slide.classList.add('swiper-slide')
      slide.setAttribute('role', 'group')
      slide.setAttribute('aria-roledescription', 'slide')
      slide.setAttribute('aria-label', slideLabels[slideIndex] ?? `Inhalt ${slideIndex + 1} von ${slides.length}`)
    })
  }

  const addPagination = () => {
    const pagination = document.createElement('div')
    pagination.classList.add(`swiper-pagination-${swiperIndex}`, 'swiper-pagination')
    suiSwiper.appendChild(pagination)
  }

  const createLiveRegion = () => {
    const liveRegion = document.createElement('div')
    liveRegion.className = 'sr-only'
    liveRegion.setAttribute('aria-live', 'polite')
    liveRegion.setAttribute('aria-atomic', 'true')
    suiSwiper.appendChild(liveRegion)

    return (e) => {
      liveRegion.textContent = slideLabels[e.realIndex]
        ? `Inhalt ${e.realIndex + 1} wird angezeigt: ${slideLabels[e.realIndex]}`
        : `Inhalt ${e.realIndex + 1} wird angezeigt`
    }
  }

  const initializeSwiper = () => {
    const handleLiveRegion = createLiveRegion()
    const swiperInstance = new Swiper(suiSwiper, {
      direction: 'horizontal',
      loop: true,
      spaceBetween: 5,
      centeredSlides: false,
      slidesPerView: 1,
      autoHeight: false,
      modules: [Navigation, Pagination],
      navigation: {
        nextEl: `.${nextButtonClass}`,
        prevEl: `.${prevButtonClass}`,
      },
      breakpoints: getBreakpointsOptions(),
      pagination: getPaginationOptions(),
      on: { slideChange: handleLiveRegion },
    })

    swiperInstances.set(suiSwiper, swiperInstance)
  }

  createBase()
  addSlides()
  addButtons()
  if (hasPagination) addPagination()
  initializeSwiper()
}

document.addEventListener('DOMContentLoaded', () => {
  const suiSwipers = document.getElementsByClassName('sui-swiper')

  Array.from(suiSwipers).forEach((suiSwiper, index) => {
    createSwiperInstance(suiSwiper, index)
  })
})
